import React, { useState } from 'react';
import { X, Folder, ChevronRight, ChevronDown, Home } from 'lucide-react';
import { FileItem } from '../types';

interface FolderSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (folderId?: string) => void;
  files: FileItem[];
  currentItemId: string;
  title: string;
}

interface FolderTreeItemProps {
  folder: FileItem;
  files: FileItem[];
  onSelect: (folderId: string) => void;
  level: number;
  currentItemId: string;
  selectedItemIds: string[];
}

// Helper function to check if a folder is a descendant of any selected items
const isDescendantOfSelected = (folderId: string, selectedItemIds: string[], files: FileItem[]): boolean => {
  for (const selectedId of selectedItemIds) {
    if (isDescendantOf(folderId, selectedId, files)) {
      return true;
    }
  }
  return false;
};

// Helper function to check if a folder is a descendant of another folder
const isDescendantOf = (descendantId: string, ancestorId: string, files: FileItem[]): boolean => {
  if (descendantId === ancestorId) return true;
  
  let currentId = descendantId;
  const visited = new Set<string>();
  
  while (currentId) {
    if (visited.has(currentId)) break;
    visited.add(currentId);
    
    const currentFolder = files.find(f => f.id === currentId);
    if (!currentFolder || !currentFolder.parentId) break;
    
    currentId = currentFolder.parentId;
    if (currentId === ancestorId) return true;
  }
  
  return false;
};

const FolderTreeItem: React.FC<FolderTreeItemProps> = ({
  folder,
  files,
  onSelect,
  level,
  currentItemId,
  selectedItemIds,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const subfolders = files.filter(file =>
    file.type === 'folder' &&
    file.parentId === folder.id &&
    !file.trashed
  );
  const hasSubfolders = subfolders.length > 0;
  const isSelectedItem = selectedItemIds.includes(folder.id);
  const isInvalidTarget = isSelectedItem || isDescendantOfSelected(folder.id, selectedItemIds, files);

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasSubfolders) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <>
      <li>
        <div className="flex items-center">
          <button
            onClick={handleToggleExpand}
            className={`p-1 rounded hover:bg-accent text-muted-foreground transition-colors duration-200 ${
              hasSubfolders ? '' : 'invisible'
            }`}
            style={{ marginLeft: `${level * 16}px` }}
          >
            {hasSubfolders && (
              isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
            )}
          </button>
          <button
            onClick={() => onSelect(folder.id)}
            disabled={isInvalidTarget}
            className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg transition-colors duration-200 text-left ${
              isInvalidTarget
                ? 'bg-muted text-muted-foreground cursor-not-allowed opacity-60'
                : 'text-foreground hover:bg-workspace-accent/10 hover:text-workspace-accent'
            }`}
          >
            <Folder size={16} className={`flex-shrink-0 ${isInvalidTarget ? 'text-muted-foreground' : 'text-workspace-accent'}`} />
            <span className="text-sm truncate">{folder.name}</span>
            {isSelectedItem && (
              <span className="text-xs text-muted-foreground ml-auto">(selected)</span>
            )}
            {!isSelectedItem && isInvalidTarget && (
              <span className="text-xs text-muted-foreground ml-auto">(invalid)</span>
            )}
          </button>
        </div>
      </li>
      {isExpanded && hasSubfolders && (
        <>
          {subfolders.map((subfolder) => (
            <FolderTreeItem
              key={subfolder.id}
              folder={subfolder}
              files={files}
              onSelect={onSelect}
              level={level + 1}
              currentItemId={currentItemId}
              selectedItemIds={selectedItemIds}
            />
          ))}
        </>
      )}
    </>
  );
};

export const FolderSelectionModal: React.FC<FolderSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  files,
  currentItemId,
  title,
}) => {
  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>();

  // Parse selected item IDs - handle both single and bulk operations
  const selectedItemIds = React.useMemo(() => {
    if (!currentItemId) return [];
    
    // For bulk operations, currentItemId might be empty but we need to get from somewhere else
    // Let's check if we have comma-separated IDs
    if (currentItemId.includes(',')) {
      return currentItemId.split(',').filter(id => id.trim() !== '');
    }
    
    // Single item
    return currentItemId ? [currentItemId] : [];
  }, [currentItemId]);
  
  if (!isOpen) return null;
  
  const folders = files.filter(file => 
    file.type === 'folder' && 
    !file.trashed
  );
  const rootFolders = folders.filter(folder => !folder.parentId);

  const handleSelect = () => {
    onSelect(selectedFolderId);
    onClose();
  };

  const handleFolderClick = (folderId: string) => {
    // Check if this folder is invalid before allowing selection
    const isSelectedItem = selectedItemIds.includes(folderId);
    const isDescendant = isDescendantOfSelected(folderId, selectedItemIds, files);
    const isInvalid = isSelectedItem || isDescendant;
    
    if (isInvalid) {
      return; // Don't allow selection of invalid folders
    }
    setSelectedFolderId(folderId);
  };

  const handleRootSelect = () => {
    setSelectedFolderId(undefined);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-md max-h-[70vh] flex flex-col border border-border">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors duration-200"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-1">
            <button
              onClick={handleRootSelect}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors duration-200 text-left ${
                selectedFolderId === undefined
                  ? 'bg-workspace-accent/10 text-workspace-accent border border-workspace-accent'
                  : 'text-foreground hover:bg-workspace-accent/10 hover:text-workspace-accent'
              }`}
            >
              <Home size={16} className="text-workspace-accent flex-shrink-0" />
              <span className="text-sm font-medium">My Drive (Root)</span>
            </button>

            {rootFolders.length > 0 && (
              <ul className="space-y-0.5 mt-2">
                {rootFolders.map((folder) => (
                  <FolderTreeItem
                    key={folder.id}
                    folder={folder}
                    files={files}
                    onSelect={handleFolderClick}
                    level={0}
                    currentItemId={currentItemId}
                    selectedItemIds={selectedItemIds}
                  />
                ))}
              </ul>
            )}

            {rootFolders.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Folder size={48} className="mx-auto mb-2 text-muted-foreground/40" />
                <p>No folders available</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/50">
          <div className="text-sm text-muted-foreground">
            {selectedFolderId === undefined ? (
              <span>Moving to: <strong className="text-foreground">My Drive</strong></span>
            ) : (
              <span>Moving to: <strong className="text-foreground">{files.find(f => f.id === selectedFolderId)?.name}</strong></span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSelect}
              className="px-6 py-2 bg-workspace-accent hover:bg-workspace-accent-hover text-white rounded-lg transition-colors duration-200"
            >
              Move Here
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

